<template>
  <aside class="accruals-estimation-rules-list" data-testid="accruals-configuration-page__rules-list">
    <header class="accruals-estimation-rules-list__header">
      <div class="accruals-estimation-rules-list__title-row">
        <h2 class="accruals-estimation-rules-list__title">Rule</h2>
        <span class="accruals-estimation-rules-list__count">{{ orderedRules.length }}</span>
      </div>
      <base-button
        v-if="!readOnly"
        primary
        small
        class="shadow-off primaryBtn accruals-estimation-rules-list__add-btn"
        label="Rule"
        :image="assets.ICON_PLUS_WHITE"
        image-alt=""
        data-testid="accruals-configuration-page__add-rule-btn"
        @click="$emit('add-rule')"
      />
    </header>

    <draggable
      v-model="orderedRules"
      :disabled="readOnly"
      handle=".accruals-estimation-rules-list__drag"
      tag="div"
      class="accruals-estimation-rules-list__items"
      ghost-class="accruals-estimation-rules-list__item--ghost"
      chosen-class="accruals-estimation-rules-list__item--chosen"
      drag-class="accruals-estimation-rules-list__item--dragging"
      :animation="180"
      data-testid="accruals-configuration-page__rules-list-items"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <div
        v-for="(rule, index) in orderedRules"
        :key="ruleKey(rule)"
        class="accruals-estimation-rules-list__item"
        :class="{ 'accruals-estimation-rules-list__item--selected': ruleKey(rule) === selectedRuleId }"
        :data-testid="`accruals-configuration-page__rule-item--${index}`"
        @click="onSelect(rule)"
      >
        <img
          v-if="!readOnly"
          :src="assets.ICON_DRAG_HANDLE"
          alt=""
          class="accruals-estimation-rules-list__drag"
          draggable="false"
          aria-hidden="true"
          :data-testid="`accruals-configuration-page__rule-drag--${index}`"
          @click.stop
        />
        <span class="accruals-estimation-rules-list__index">{{ index + 1 }}</span>
        <span class="accruals-estimation-rules-list__content">
          <span class="accruals-estimation-rules-list__name">{{ rule.name }}</span>
          <span v-if="rule.badge" class="accruals-estimation-rules-list__badge">{{ rule.badge }}</span>
        </span>
      </div>
    </draggable>
  </aside>
</template>

<script>
import draggable from 'vuedraggable';
import { ACCRUALS_CONFIG_ASSETS } from '../accrualsConfigurationConstants';
import { estimationRuleId } from '../accrualsEstimationRuleUtils';

export default {
  name: 'AccrualsEstimationRulesList',
  components: { draggable },
  props: {
    rules: { type: Array, default: () => [] },
    selectedRuleId: { type: String, default: '' },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      assets: ACCRUALS_CONFIG_ASSETS,
      orderedRules: [],
      isDragging: false,
    };
  },
  watch: {
    rules: {
      immediate: true,
      handler(next) {
        if (this.isDragging) return;
        this.syncOrderedRules(next);
      },
    },
  },
  beforeDestroy() {
    this.orderedRules = [];
    this.isDragging = false;
  },
  methods: {
    ruleKey(rule) {
      return estimationRuleId(rule);
    },
    syncOrderedRules(next) {
      const incoming = Array.isArray(next) ? next : [];
      if (!this.orderedRules.length) {
        this.orderedRules = incoming.slice();
        return;
      }
      const byId = new Map(incoming.map((rule) => [this.ruleKey(rule), rule]));
      const ordered = this.orderedRules
        .map((rule) => byId.get(this.ruleKey(rule)))
        .filter(Boolean);
      incoming.forEach((rule) => {
        if (!ordered.some((item) => this.ruleKey(item) === this.ruleKey(rule))) {
          ordered.push(rule);
        }
      });
      this.orderedRules = ordered;
    },
    onSelect(rule) {
      if (this.isDragging) return;
      this.$emit('select', this.ruleKey(rule));
    },
    onDragStart() {
      this.isDragging = true;
    },
    onDragEnd() {
      this.$emit('reorder', this.orderedRules.slice());
      this.$nextTick(() => {
        this.isDragging = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimationRulesList.scss';
</style>
